import React, { useState, useEffect } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { enquiry } from '../../../helper/api_url';
import { formatDate } from '../../../helper/formatDate';
import { swal } from '../../../helper/swal';
import { getStatusBadge } from './EnquiryList/enquiryUtils';

export function FollowUpHistoryModal({ enquiryId, schoolName, children }) {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);

    const toggleModal = () => setShow(!show);

    useEffect(() => {
        if (show && enquiryId) {
            setLoading(true);
            enquiry.fetchSingle(enquiryId)
                .then((res) => {
                    const enquiryData = res.data?.enquiry;
                    if (enquiryData?.followups && Array.isArray(enquiryData.followups)) {
                        const sorted = [...enquiryData.followups].sort(
                            (a, b) => new Date(b.created_at) - new Date(a.created_at)
                        );
                        setHistory(sorted);
                    } else {
                        setHistory([]);
                    }
                })
                .catch((err) => {
                    swal.error(err.response?.data?.message || err.message || 'Failed to load follow-up history');
                })
                .finally(() => setLoading(false));
        }
    }, [show, enquiryId]);

    return (
        <>
            {children ? (
                <div onClick={toggleModal} style={{ cursor: 'pointer' }}>
                    {children}
                </div>
            ) : (
                <button
                    type="button"
                    onClick={toggleModal}
                    className="btn btn-sm btn-soft-primary py-1 px-2"
                    title="Follow-Up & SMS History"
                >
                    <i className="ri-chat-3-line fs-13" />
                </button>
            )}

            <Modal className="fade" centered show={show} onHide={toggleModal} size="md">
                {/* Clean Header */}
                <Modal.Header closeButton className="border-bottom py-3 px-4 bg-light-subtle">
                    <div>
                        <h5 className="modal-title fs-15 fw-bold text-dark mb-0.5">Follow-Up & SMS History</h5>
                        <div className="d-flex align-items-center gap-2 mt-0.5 flex-wrap">
                            <span className="text-muted fs-12">
                                School: <strong className="text-dark">{schoolName || 'Candidate'}</strong>
                            </span>
                            {history.length > 0 && (
                                <span className="badge bg-soft-primary text-primary fs-11 px-2 py-0.5">
                                    {history.length} {history.length === 1 ? 'Record' : 'Records'}
                                </span>
                            )}
                        </div>
                    </div>
                </Modal.Header>

                {/* Modal Body with Full-Width Readable Cards */}
                <Modal.Body className="p-3 p-md-4" style={{ maxHeight: '520px', overflowY: 'auto' }}>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" role="status" size="sm" />
                            <p className="text-muted fs-13 mt-2 mb-0">Loading follow-up history...</p>
                        </div>
                    ) : history.length > 0 ? (
                        <div className="d-flex flex-column gap-3 w-100">
                            {history.map((item, index) => {
                                const status = getStatusBadge(item.status);
                                const isLatest = index === 0;

                                return (
                                    <div key={item.id || index} className="card border shadow-sm rounded-3 mb-0 w-100">
                                        {/* Card Header: Date & Status */}
                                        <div className="card-header bg-light-subtle py-2 px-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="fw-bold text-dark fs-13">
                                                    <i className="ri-calendar-event-line text-muted me-1" />
                                                    {formatDate(item.created_at)}
                                                </span>
                                                {isLatest && (
                                                    <span className="badge bg-soft-success text-success fs-10 px-1.5 py-0.5">
                                                        Latest Note
                                                    </span>
                                                )}
                                            </div>
                                            <span className={`badge bg-soft-${status.color} text-${status.color} fs-11 px-2.5 py-1 text-capitalize fw-semibold`}>
                                                {item.status === 'deleted' ? 'Not Interested' : item.status || 'Pending'}
                                            </span>
                                        </div>

                                        {/* Card Body: Remark content */}
                                        <div className="card-body p-3">
                                            <div className="p-2.5 bg-light rounded border border-light-subtle fs-13 text-dark lh-base" style={{ whiteSpace: 'pre-wrap' }}>
                                                {item.remark || <span className="text-muted fst-italic">No remarks recorded.</span>}
                                            </div>

                                            {/* Next Scheduled Follow-Up */}
                                            {item.joining_date && (
                                                <div className="d-flex align-items-center justify-content-between mt-2.5 pt-2 border-top border-light-subtle fs-12">
                                                    <span className="text-muted d-inline-flex align-items-center gap-1">
                                                        <i className="ri-time-line text-primary" />
                                                        <span>Next Follow-Up Date:</span>
                                                    </span>
                                                    <span className="fw-bold text-primary fs-13">
                                                        {formatDate(item.joining_date)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-5 my-2">
                            <div className="avatar-sm mx-auto mb-3">
                                <span className="avatar-title rounded-circle bg-light text-muted fs-24">
                                    <i className="ri-chat-history-line" />
                                </span>
                            </div>
                            <h6 className="fs-14 fw-semibold text-dark mb-1">No Follow-Up Records Found</h6>
                            <p className="text-muted fs-13 mb-0">No past conversation notes or messages have been logged for this candidate.</p>
                        </div>
                    )}
                </Modal.Body>

                {/* Clean Footer */}
                <Modal.Footer className="border-top py-2.5 px-4 bg-light-subtle d-flex align-items-center justify-content-between">
                    <small className="text-muted fs-12">
                        {history.length > 0 ? `Total ${history.length} records found` : ''}
                    </small>
                    <button type="button" className="btn btn-sm btn-light px-3" onClick={toggleModal}>
                        Close
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default FollowUpHistoryModal;
